import React from 'react';
import {
  Box,
  Grid,
  Card,
  Skeleton,
  Container,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

export const PropertyIntelligenceSkeleton: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: theme.palette.mode === 'dark' ? '#0a0a0a' : theme.palette.background.default,
        color: theme.palette.text.primary,
      }}
    >
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header with Back Button */}
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mb: 2 }} />
        </Box>

        {/* Main Header Card */}
        <Card
          sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            mb: 4,
            boxShadow: theme.shadows[8],
          }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Skeleton variant="text" width={200} height={24} sx={{ mb: 2 }} />

              {/* Score and Badge */}
              <Box sx={{ display: 'flex', alignItems: 'end', gap: 3, mb: 2 }}>
                <Skeleton variant="text" width={120} height={96} />
                <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 2 }} />
              </Box>

              <Skeleton variant="text" width={300} height={28} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={250} height={20} sx={{ mb: 3 }} />

              {/* Progress Bar */}
              <Skeleton variant="rectangular" width={600} height={6} sx={{ borderRadius: 3 }} />
            </Grid>

            <Grid item xs={12} md={4}>
              <Grid container spacing={2}>
                {[1, 2, 3, 4].map((index) => (
                  <Grid item xs={6} key={index}>
                    <Card
                      sx={{
                        backgroundColor: alpha(theme.palette.background.paper, 0.5),
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 3,
                        p: 2.5,
                        height: '100%',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Skeleton variant="circular" width={8} height={8} />
                        <Skeleton variant="text" width="80%" height={20} />
                      </Box>
                      <Skeleton variant="text" width="100%" height={16} />
                      <Skeleton variant="text" width="90%" height={16} />
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 3 }} />
            <Skeleton variant="rectangular" width={140} height={40} sx={{ borderRadius: 3 }} />
            <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 3 }} />
          </Box>
        </Card>

        {/* Drivers and Sidebar */}
        <Grid container spacing={4}>
          <Grid item xs={12} lg={8}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Skeleton variant="text" width={200} height={28} />
                <Skeleton variant="text" width={100} height={20} />
              </Box>

              <Grid container spacing={3}>
                {[1, 2, 3, 4, 5].map((index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card
                      sx={{
                        backgroundColor: alpha(theme.palette.background.paper, 0.4),
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 3,
                        p: 3,
                      }}
                    >
                      {/* Header */}
                      <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 3, mb: 2 }}>
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Skeleton variant="text" width={120} height={20} />
                            <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 2 }} />
                          </Box>
                          <Skeleton variant="text" width="90%" height={16} />
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Skeleton variant="text" width={40} height={32} />
                        </Box>
                      </Box>

                      {/* Progress */}
                      <Skeleton variant="rectangular" width="100%" height={6} sx={{ borderRadius: 3, mb: 2 }} />

                      {/* Footer */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Skeleton variant="text" width={150} height={16} />
                        <Skeleton variant="text" width={80} height={16} />
                      </Box>

                      {/* Action Button */}
                      <Skeleton variant="rectangular" width="100%" height={36} sx={{ borderRadius: 2 }} />
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12} lg={4}>
            {/* Top Opportunity Skeleton */}
            <Card
              sx={{
                backgroundColor: alpha(theme.palette.background.paper, 0.4),
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 3,
                p: 3,
                mb: 3,
              }}
            >
              <Skeleton variant="text" width={120} height={24} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="90%" height={16} sx={{ mb: 3 }} />

              <Card
                sx={{
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: 2.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Skeleton variant="text" width={100} height={20} />
                  <Skeleton variant="text" width={30} height={24} />
                </Box>

                <Skeleton variant="rectangular" width="100%" height={6} sx={{ borderRadius: 3, mb: 2 }} />
                <Skeleton variant="text" width="85%" height={16} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 2 }} />
              </Card>
            </Card>

            {/* Risk Flags Skeleton */}
            <Card
              sx={{
                backgroundColor: alpha(theme.palette.background.paper, 0.4),
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 3,
                p: 3,
              }}
            >
              <Skeleton variant="text" width={80} height={24} sx={{ mb: 3 }} />

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {[1, 2, 3].map((index) => (
                  <Skeleton key={index} variant="rectangular" width={120} height={28} sx={{ borderRadius: 2 }} />
                ))}
              </Box>

              <Skeleton variant="text" width="90%" height={12} />
            </Card>
          </Grid>
        </Grid>

        {/* Methodology Section Skeleton */}
        <Box sx={{ mt: 4 }}>
          <Card
            sx={{
              backgroundColor: alpha(theme.palette.background.paper, 0.3),
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 3,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton variant="text" width={200} height={20} />
              <Skeleton variant="circular" width={24} height={24} />
            </Box>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};

export default PropertyIntelligenceSkeleton;